/**
 * Gets the active subscriptions for the user (customer) in Stripe
 * @param customerId The customer ID in Stripe
 */
export async function getActiveSubscriptions(customerId: string) {
  if (!customerId || customerId === "UNASSIGNED") return null;

  const response = await fetch(
    `/api/stripe/subscription?customerId=${customerId}&_=${Date.now()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to fetch active subscriptions");
  }

  const { data } = await response.json();
  return data.map((sub: any) => ({
    id: sub.id,
    seats: sub.quantity,
    cancelAt: sub.cancel_at || null,
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    canceledAt: sub.canceled_at || null,
    productId: sub.plan.product,
    priceId: sub.plan.id,
    status: sub.status,
  }));
}

/**
 * Gets the checkout URL for the user (customer) in Stripe
 * @param customerId The customer ID in Stripe
 * @param priceId The price ID in Stripe
 */
export async function getCheckoutUrl(
  customerId: string,
  priceId: string,
): Promise<string> {
  const response = await fetch("/api/stripe/checkout", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ customerId, priceId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to create checkout session for subscription",
    );
  }

  const data = await response.json();
  return data.url || "";
}

/**
 * Creates a Stripe billing portal session for the user (customer)
 * @param customerId The customer ID in Stripe
 */
export async function getSubscriptionUrl(customerId: string): Promise<string> {
  const returnUrl = `/settings/subscriptions`;

  const response = await fetch("/api/stripe/portal", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ returnUrl, customerId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error || "Failed to create portal session for subscription",
    );
  }

  const data = await response.json();
  return data.url || "";
}
